"use client";

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = [
  {
    title: "🔍 Initializing Visual Audit...",
    detail: "Scanning Image 1 (Car) and Image 2 (Reference).",
  },
  {
    title: "🔢 Counting Spokes...",
    detail: "Trace complete. Verifying geometric alignment.",
  },
  {
    title: "🎨 Extracting Finish...",
    detail: "Detecting material (Alloy/Chrome) and light reflections.",
  },
  {
    title: "📐 Perspective Mapping...",
    detail: "Adjusting wheel offset to match the car's stance.",
  },
  {
    title: "✨ Polishing Render...",
    detail: "Finalizing shadows and removing logos from center caps.",
  },
] as const;

const TRIVIA = [
  "Weight matters: Dropping ~12 lbs per wheel can shave ~0.3s off 0-60 mph.",
  "Karl Benz's first gasoline car used wire-spoke wheels like a bicycle.",
  "Many premium wheel designs follow the Fibonacci sequence for spoke spacing.",
  "\"Mesh\" or multi-spoke designs help cool brakes by adding surface area.",
  "Unsprung weight matters: lighter wheels let suspension react up to 30% faster.",
] as const;

interface LoadingVisualAuditProps {
  className?: string;
}

export function LoadingVisualAudit({ className }: LoadingVisualAuditProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [triviaIndex, setTriviaIndex] = useState(0);
  const [showExtendedWaitMessage, setShowExtendedWaitMessage] = useState(false);

  // Advance through the five-step script, then hold on the final step.
  useEffect(() => {
    const stepInterval = setInterval(() => {
      setStepIndex((prev) => (prev < STEPS.length - 1 ? prev + 1 : prev));
    }, 5200);
    return () => clearInterval(stepInterval);
  }, []);

  // Rotate trivia to keep the wait interesting.
  useEffect(() => {
    const triviaInterval = setInterval(() => {
      setTriviaIndex((prev) => (prev + 1) % TRIVIA.length);
    }, 7200);
    return () => clearInterval(triviaInterval);
  }, []);

  // Show extended wait message after 40 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowExtendedWaitMessage(true);
    }, 40000); // 40 seconds
    return () => clearTimeout(timeout);
  }, []);

  const progress = ((stepIndex + 1) / STEPS.length) * 100;
  const step = STEPS[stepIndex];
  const trivia = TRIVIA[triviaIndex];

  return (
    <div
      className={cn(
        "w-full rounded-lg bg-muted/70 border border-muted-foreground/20",
        "flex flex-col justify-between min-h-[320px]",
        className
      )}
    >
      <div className="p-6 flex-1 flex flex-col justify-center text-center gap-4">
        <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Visual Audit</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
            Step {stepIndex + 1} of {STEPS.length}
          </span>
        </div>

        <div className="space-y-2">
          <p className="text-lg font-semibold text-foreground">{step.title}</p>
        </div>

        <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden border border-white/10">
          <div
            className="h-full bg-linear-to-r from-blue-500 via-cyan-400 to-emerald-400 transition-[width]"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="text-xs text-muted-foreground">
          Est. 15–40s • Progress updates auto-advance while we render your wheel swap.
        </div>

        {showExtendedWaitMessage && (
          <div className="mt-3 px-4 py-2 rounded-md bg-amber-500/10 border border-amber-500/20">
            <p className="text-sm text-amber-200/90">
              Things are extra busy at the moment with lots of people generating. Appreciate your patience!
            </p>
          </div>
        )}
      </div>

      <div className="bg-black/40 border-t border-white/5 px-4 py-3 backdrop-blur-sm">
        <div className="text-[11px] uppercase tracking-[0.16em] text-blue-200/70 mb-1">
          Did you know?
        </div>
        <div className="text-sm text-blue-50/90">{trivia}</div>
      </div>
    </div>
  );
}

